import { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format } from 'date-fns';
import { METRICS, type MetricKey } from '@raptor/shared';
import { LoadingState } from '@/components/LoadingState';
import { useWellReadings } from '../api';
import { bucketReadings, bucketSizeMs } from '../aggregate';
import type { ReadingsRange } from '../types';

interface Props {
  wellId: string;
  metric: MetricKey;
  range: ReadingsRange;
}

// Time-series line chart for one metric. States and the truncation notice are
// handled here; sparse series (e.g. 2 readings/day) render with visible dots so
// a handful of points is still legible.
//
// Readings are averaged into time buckets sized from the range span (see
// aggregate.ts) — a 15-minute reporting device would otherwise put 672 points
// in the 7d view and draw a band rather than a line.
export function ReadingsChart({ wellId, metric, range }: Props) {
  const cfg = METRICS[metric];
  const query = useWellReadings(wellId, range, [metric]);

  const spanMs = new Date(range.to).getTime() - new Date(range.from).getTime();
  const bucketMs = bucketSizeMs(spanMs);

  const data = useMemo(
    () => bucketReadings(query.data?.readings ?? [], metric, bucketMs),
    [query.data, metric, bucketMs],
  );

  if (query.isLoading) {
    return <LoadingState rows={3} barClassName="h-8" />;
  }
  if (query.isError) {
    return (
      <div className="flex h-[240px] items-center justify-center text-sm text-destructive">
        Couldn&apos;t load readings.
      </div>
    );
  }
  if (data.length === 0) {
    return (
      <div className="flex h-[240px] items-center justify-center text-sm text-muted-foreground">
        No readings in this range
      </div>
    );
  }

  const spanIsDay = spanMs <= 36 * 60 * 60 * 1000;
  const fmtTick = (t: number) => format(new Date(t), spanIsDay ? 'HH:mm' : 'd MMM');

  return (
    <div data-testid="readings-chart">
      {query.data?.truncated && (
        <div
          className="mb-2 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-900"
          data-testid="readings-chart-truncated"
        >
          Showing the most recent {query.data.readings.length} readings for this range
          {query.data.effectiveFrom &&
            ` (${format(new Date(query.data.effectiveFrom), 'd MMM HH:mm')}–${format(
              new Date(query.data.effectiveTo ?? query.data.effectiveFrom),
              'd MMM HH:mm',
            )})`}
          .
        </div>
      )}
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
          <XAxis
            dataKey="t"
            type="number"
            scale="time"
            domain={['dataMin', 'dataMax']}
            tickFormatter={fmtTick}
            tick={{ fontSize: 11 }}
          />
          <YAxis
            tickFormatter={(v: number) => v.toFixed(cfg.precision)}
            width={48}
            tick={{ fontSize: 11 }}
            label={{ value: cfg.unit, angle: -90, position: 'insideLeft', fontSize: 11 }}
          />
          <Tooltip
            labelFormatter={(t) => format(new Date(t as number), 'd MMM yyyy HH:mm')}
            // Values are bucket means, so say so rather than letting them read
            // as individual samples. Suppressed at count 1, where there is no
            // averaging to disclose (sparse series, or a part-filled bucket).
            formatter={(v: number, _name, entry) => {
              const count = (entry?.payload as { count?: number } | undefined)?.count ?? 1;
              const suffix = count > 1 ? ` (mean of ${count})` : '';
              return [`${v.toFixed(cfg.precision)} ${cfg.unit}${suffix}`, cfg.label];
            }}
          />
          <Line
            type="monotone"
            dataKey="value"
            className="stroke-primary"
            strokeWidth={2}
            dot={{ r: 2 }}
            connectNulls
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
